import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { BannerNotification, ButtonBar } from '@frontend/design-system';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { AudienceTray } from './audience-tray';

type Props = PropsWithChildren<{
  duplicateRecipientsCount: number;
  segmentsCount: number;
  segmentsRecipientsCount: number;
  selectedListsCount: number;
  selectedListsRecipientsCount: number;
}>;

export const AudienceTraySharedFooter = ({
  children,
  duplicateRecipientsCount,
  segmentsCount,
  segmentsRecipientsCount,
  selectedListsCount,
  selectedListsRecipientsCount,
}: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { locationIds, mode, view } = useAudienceTray();
  const totalRecipientsCount = segmentsRecipientsCount + selectedListsRecipientsCount;

  const description = useMemo(() => {
    if (selectedListsRecipientsCount && segmentsRecipientsCount) return t('From Segments and Saved Lists');
    if (selectedListsRecipientsCount) return t('From Saved Lists');
    if (segmentsRecipientsCount) return t('From Segments');
    if (view === 'lists') return t('From Saved Lists');
    return t('From Segments');
  }, [selectedListsRecipientsCount, segmentsRecipientsCount, view, t]);

  const tooltip = useMemo(() => {
    if (selectedListsCount > 1 && segmentsCount === 0) {
      return t(
        'Identical contacts across lists will be de-duplicated upon the creation of this audience. For this reason, your final audience could be smaller than predicted here.'
      );
    }
    if (segmentsCount > 1 && selectedListsCount === 0) {
      return t(
        'Identical contacts across segments will be de-duplicated upon the creation of this audience. For this reason, your final audience could be smaller than predicted here.'
      );
    }
    if (segmentsCount && selectedListsCount) {
      return t(
        'Identical contacts across segments and lists will be de-duplicated upon the creation of this audience. For this reason, your final audience could be smaller than predicted here.'
      );
    }
    return undefined;
  }, [selectedListsCount, segmentsCount, t]);

  const isDisabled =
    mode === 'lists-create-from-lists' ||
    mode === 'lists-create-from-recipients' ||
    mode === 'lists-create-from-upload' ||
    mode === 'lists-rename-list';

  return (
    <AudienceTray.Footer>
      <AudienceTray.Footer.RecipientCount
        count={totalRecipientsCount}
        description={description}
        tooltip={tooltip}
        isDisabled={isDisabled}
      />
      {!!duplicateRecipientsCount && (
        <BannerNotification
          message={t(
            '{{count}} contacts from this audience will receive this campaign more than once, due to being associated with multiple selected locations.',
            { count: duplicateRecipientsCount }
          )}
          status='info'
          css={{ marginTop: theme.spacing(2) }}
        />
      )}
      <AudienceTray.Footer.ActionsBar>
        {locationIds.length > 1 && <AudienceTray.Footer.AllotmentsLink />}
        <ButtonBar css={{ justifyContent: 'flex-end', padding: 0 }}>{children}</ButtonBar>
      </AudienceTray.Footer.ActionsBar>
    </AudienceTray.Footer>
  );
};
